/**
 * Layer colours (SPEC-V2.md §4.5).
 *
 * A stacked strategy puts chips from several spreads on the same felt, and a
 * chip that cannot say which spread it came from makes the whole stack
 * unreadable. Each layer therefore gets a hue, keyed on its position in the
 * stack.
 *
 * **Six, and no more.** Not a roulette constraint — a perceptual one. Past six
 * the hues stop being tellable apart at chip size, and a legend that needs
 * study is not a legend.
 *
 * On which hues: the ramp deliberately does *not* try to dodge every colour
 * already in use. Chips on the felt and pocket states on the wheel never share
 * a surface, so a layer wearing the same violet as a placed chip reads as a
 * chip, which is what it is. What the ramp does avoid is **green and rose**,
 * because those two carry meaning everywhere else in the app — profit and loss
 * — and a layer that happened to be green would imply something about its
 * performance that its position in a list cannot possibly know.
 *
 * Colours are literal here rather than CSS variables because they are a
 * sequence, not a set of named roles: the nth layer needs the nth hue, and a
 * lookup by index is the whole interface. They are chosen to hold up on both
 * the dark surface used throughout and against the felt's green.
 */

export const MAX_LAYER_COLOURS = 6;

export interface LayerColour {
  /** Fill for a chip band and the legend swatch. */
  readonly hue: string;
  /** Border, a little stronger, so a band edge survives on a dark felt. */
  readonly edge: string;
  /** Text that sits on `hue`. */
  readonly ink: string;
  /** Plain-language name, for a tooltip or a screen reader. */
  readonly label: string;
}

const RAMP: readonly LayerColour[] = [
  { hue: '#8b5cf6', edge: '#a78bfa', ink: '#ffffff', label: 'violet' },
  { hue: '#38bdf8', edge: '#7dd3fc', ink: '#052a3a', label: 'sky' },
  { hue: '#fbbf24', edge: '#fcd34d', ink: '#3a2a02', label: 'amber' },
  { hue: '#ec4899', edge: '#f472b6', ink: '#ffffff', label: 'pink' },
  { hue: '#14b8a6', edge: '#2dd4bf', ink: '#022f2b', label: 'teal' },
  { hue: '#f97316', edge: '#fb923c', ink: '#3a1a02', label: 'orange' },
];

/**
 * The colour for a layer at this stack position.
 *
 * Wraps rather than throwing past the cap, so a strategy that somehow carries
 * more layers than the composer allows still renders. It will be ambiguous, but
 * an ambiguous picture beats a crash and the cap is enforced where strategies
 * are built.
 */
export function layerColour(index: number): LayerColour {
  return RAMP[index % RAMP.length]!;
}

export const LAYER_COLOURS: readonly LayerColour[] = RAMP;
